import { useAuth } from 'src/lib/auth.store';
import { Config } from '../../config';
import { UploadData, UploadRequest } from '../models/upload';

const API_URL = Config.apiUrl;

async function handleApiError(error: unknown, options?: RequestOptions) {
  if (error instanceof Response) {
    const errText = await error.text();
    let errBody: any;

    try {
      errBody = JSON.parse(errText);
    } catch (_) {
      errBody = errText;
    }

    const newError = {
      status: error?.status,
      error: errBody,
    };

    if (!options?.skipErrorCheckUnauthorised && [401, 403].includes(newError.status)) {
      useAuth.getState().logout();
    }

    return newError;
  } else {
    console.error('Unknown API error', error);
    return error;
  }
}

function parseRequestOptionsFetch(options: RequestOptions | undefined): RequestInit {
  return {
    headers: {
      'Content-Type': 'application/json',
      ...(useAuth.getState().token && {
        Authorization: `Bearer ${useAuth.getState().token}`,
      }),
      ...(options?.apiKey && {
        Authorization: `ApiKey ${options?.apiKey}`,
      }),
    },
  };
}

interface RequestOptions {
  query?: Record<string, any>;
  apiKey?: string | null;
  skipErrorCheckUnauthorised?: boolean;
}

export class ApiUtils {
  public static async get<T>(url: string, options?: RequestOptions): Promise<T> {
    try {
      if (options?.query) {
        url = url + '?' + new URLSearchParams(options.query).toString();
      }
      const opts = parseRequestOptionsFetch(options);
      const resp = await fetch(`${API_URL}/${url}`, {
        method: 'GET',
        ...opts,
      });
      if (resp.status >= 400) throw resp;
      const json = await resp.json();
      return json as T;
    } catch (error) {
      throw await handleApiError(error, options);
    }
  }

  public static async post<T>(url: string, body?: any, options?: RequestOptions): Promise<T> {
    try {
      if (options?.query) {
        url = url + '?' + new URLSearchParams(options.query).toString();
      }
      const opts = parseRequestOptionsFetch(options);
      const resp = await fetch(`${API_URL}/${url}`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
        ...opts,
      });
      if (resp.status >= 400) throw resp;
      const json = await resp.json();
      return json as T;
    } catch (error) {
      throw await handleApiError(error, options);
    }
  }

  public static async put<T>(url: string, body?: any, options?: RequestOptions): Promise<T> {
    try {
      if (options?.query) {
        url = url + '?' + new URLSearchParams(options.query).toString();
      }
      const opts = parseRequestOptionsFetch(options);
      const resp = await fetch(`${API_URL}/${url}`, {
        method: 'PUT',
        body: JSON.stringify(body ?? {}),
        ...opts,
      });
      if (resp.status >= 400) throw resp;
      const json = await resp.json();
      return json as T;
    } catch (error) {
      throw await handleApiError(error, options);
    }
  }

  public static async patch<T>(url: string, body?: any, options?: RequestOptions): Promise<T> {
    try {
      if (options?.query) {
        url = url + '?' + new URLSearchParams(options.query).toString();
      }
      const opts = parseRequestOptionsFetch(options);
      const resp = await fetch(`${API_URL}/${url}`, {
        method: 'PATCH',
        body: JSON.stringify(body ?? {}),
        ...opts,
      });
      if (resp.status >= 400) throw resp;
      const json = await resp.json();
      return json as T;
    } catch (error) {
      throw await handleApiError(error, options);
    }
  }

  public static async delete<T>(url: string, body?: any, options?: RequestOptions): Promise<T> {
    try {
      if (options?.query) {
        url = url + '?' + new URLSearchParams(options.query).toString();
      }
      const opts = parseRequestOptionsFetch(options);
      const resp = await fetch(`${API_URL}/${url}`, {
        method: 'DELETE',
        body: JSON.stringify(body ?? {}),
        ...opts,
      });
      if (resp.status >= 400) throw resp;
      const text = await resp.text();
      return text ? JSON.parse(text) : ({} as T);
    } catch (error) {
      throw await handleApiError(error, options);
    }
  }

  public static defaultErrorMessage = 'An unkonwn error has occured. Please try again later.';

  private static errorTranslations: Record<string, string> = {
    'Driver with this phone number already exists.': 'Există deja un șofer cu acest număr de telefon.',
    'Customer with this phone number already exists.': 'Există deja un client cu acest număr de telefon.',
    'Driver not found': 'Șoferul nu a fost găsit.',
    'Customer not found': 'Clientul nu a fost găsit.',
    'Failed to fetch': 'A apărut o problemă cu solicitarea. Vă rugăm să încercați din nou mai târziu!',
    'phoneNumber must be a valid phone number': 'Numărul de telefon este invalid',
    'Path `name` is required.': 'Numele este obligatoriu.',
    'Path `subaccountId` is required.': 'Subaccount ID este obligatoriu.',
  };

  private static validationTermsRo: Record<string, string> = {
    validation: 'Validare',
    invalid: 'invalid',
    required: 'obligatoriu',
    phoneNumber: 'numărul de telefon',
    email: 'emailul',
    password: 'parola',
    fullName: 'numele complet',
    firstName: 'prenumele',
    lastName: 'numele',
    status: 'statusul',
    minLength: 'lungime minimă',
    maxLength: 'lungime maximă',
    min: 'minim',
    max: 'maxim',
    'must be': 'trebuie să fie',
    'a valid': 'valid',
    'phone number': 'număr de telefon',
  };

  private static autoTranslateValidationKey(key: string): string {
    // Check if it's a validation key pattern (e.g., validation.phoneNumber.invalid)
    if (key.startsWith('validation.')) {
      const parts = key.split('.');
      const translatedParts = parts
        .slice(1) // Skip 'validation' prefix
        .map((part) => ApiUtils.validationTermsRo[part] || part)
        .filter((part) => part !== 'Validare'); // Remove redundant validation word

      return translatedParts.join(' ');
    }

    // Handle sentence-style validation messages (e.g., "phoneNumber must be a valid phone number")
    let translated = key;

    // Replace field names at the start of the message
    for (const [eng, ro] of Object.entries(ApiUtils.validationTermsRo)) {
      const regex = new RegExp(`\\b${eng}\\b`, 'gi');
      translated = translated.replace(regex, ro);
    }

    // Capitalize first letter
    if (translated.length > 0) {
      translated = translated.charAt(0).toUpperCase() + translated.slice(1);
    }

    return translated;
  }

  public static convertApiErrorToString(error: any) {
    if (typeof error === 'string') {
      return ApiUtils.errorTranslations[error] || ApiUtils.autoTranslateValidationKey(error);
    }
    if (error?.message === 'Failed to fetch') {
      return ApiUtils.errorTranslations['Failed to fetch'];
    }
    // Check for errors array in error.error.errors (nested)
    if (typeof error === 'object' && error.error?.errors?.length) {
      const errorMsg = error.error.errors[0];
      return ApiUtils.errorTranslations[errorMsg] || ApiUtils.autoTranslateValidationKey(errorMsg);
    }
    // Check for errors array in error.errors (direct)
    if (typeof error === 'object' && error.errors?.length) {
      const errorMsg = error.errors[0];
      return ApiUtils.errorTranslations[errorMsg] || ApiUtils.autoTranslateValidationKey(errorMsg);
    }
    const errorMsg = error?.error?.message ?? error?.message ?? ApiUtils.defaultErrorMessage;
    return ApiUtils.errorTranslations[errorMsg] || ApiUtils.autoTranslateValidationKey(errorMsg);
  }

  public static async uploadFile(data: UploadRequest, options?: RequestOptions) {
    const requestObj: any = { ...data, mimetype: data.file.type };
    delete requestObj.file;

    const uploadData = await this.post<UploadData>('uploads', requestObj, options);
    const formData = new FormData();

    Object.entries(uploadData.upload.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', data.file);

    try {
      const resp = await fetch(uploadData.upload.url, {
        method: 'POST',
        body: formData,
      });
      if (resp.status >= 400) throw resp;
      const json = await resp.json();
      return json;
    } catch (error) {
      throw await handleApiError(error, options);
    }
  }
}
